import { useState } from 'react';

import type { BookingWearer, RecordFittingInput } from '@/api/bookings';
import { useEmployees } from '@/api/masters';
import { DatePicker } from '@/components/DatePicker';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Field } from '@/components/Field';
import { Button, Notice } from '@/components/PageHeading';
import { SearchPicker, type PickerOption } from '@/components/SearchPicker';
import { useEscapeKey } from '@/components/shortcuts';
import { dateTimeFormat, formatDateTime, toDayjs } from '@core/format/dates';

import { fittingProblems, fittingRequest, fittingState } from './fitting';

type Intent = 'schedule' | 'complete' | 'clear';

/**
 * Booking a wearer in to be measured, moving that appointment, or marking it done (spec Section 12).
 *
 * <p>
 * <strong>Both fitting columns were inert until the endpoint behind this existed.</strong>
 * `FittingCompletedOn` had no writer anywhere in the solution, so the alteration docket printed a
 * blank "Measured" line on every sheet and the register's unfitted count could never fall; and the
 * appointment was write-once, set when the booking was taken and never movable, so a customer who
 * rang to change the day could not be accommodated in the system at all.
 * </p>
 *
 * <p>
 * <strong>Per wearer, because one appointment covers everything that person is wearing.</strong>
 * Alteration is the other grain — only the tuxedo's sleeve goes to the tailor — and the two are
 * deliberately not the same control.
 * </p>
 *
 * <p>
 * Hand-rolled rather than a `FormDrawer` because there is no single commit to give that footer:
 * three buttons mean three different statements, and a form with one Save would have to encode
 * which of them was meant in a radio group nobody would read.
 * </p>
 */
export function FittingDrawer({
  wearer,
  branchId,
  busy,
  onClose,
  onRecord,
}: {
  wearer: BookingWearer;
  /** The booking's branch, so the staff list is the one this shop actually has. */
  branchId: number | undefined;
  busy: boolean;
  onClose: () => void;
  onRecord: (input: RecordFittingInput) => void;
}) {
  const state = fittingState(wearer);

  const [intent, setIntent] = useState<Intent>(state === 'scheduled' ? 'complete' : 'schedule');
  const [appointmentOn, setAppointmentOn] = useState<string | null>(wearer.fittingAppointmentOn);
  const [fitterId, setFitterId] = useState<number | null>(wearer.fittingByEmployeeId);
  const [fitterName, setFitterName] = useState<string | null>(wearer.fittingByEmployeeName);
  const [search, setSearch] = useState('');

  useEscapeKey(onClose, true);
  const fields = useAutoFocusFirstField<HTMLDivElement>(true);

  /*
    Everybody, not only the people ticked as `Counter`.

    Measuring a wedding party is not one of the four capabilities and never will be — it is what the
    owner does on a Saturday and what the master tailor does the rest of the week. Narrowing this
    list would leave the two people most likely to be holding the tape unfindable.
  */
  const staff = useEmployees(branchId, { search, pageSize: 10 });

  const options: PickerOption[] = (staff.data?.items ?? []).map((employee) => ({
    id: String(employee.id),
    primary: employee.fullName,
    secondary: employee.designation ?? employee.mobile,
  }));

  const chosenFitter: PickerOption | null =
    fitterId === null
      ? null
      : (options.find((option) => option.id === String(fitterId)) ?? {
          id: String(fitterId),
          primary: fitterName ?? 'Assigned',
        });

  const problems = fittingProblems(intent, { appointmentOn });

  return (
    <div data-shortcut-layer className="fixed inset-0 z-9999 flex print:hidden">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="flex-1 cursor-default bg-[var(--overlay-scrim)]"
      />

      <aside
        {...enterKeyScope}
        className="flex w-full max-w-[560px] flex-col border-s border-[var(--overlay-border)] bg-[var(--overlay-surface)]"
      >
        <header className="border-border-color flex items-center justify-between border-b px-5 py-4">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-gray-900">Fitting</p>
            <p className="text-default truncate text-[12px]">
              {wearer.name}
              {wearer.relation ? ` · ${wearer.relation}` : ''}
            </p>
          </div>
          <Button icon="icon icon-x" onClick={onClose} disabled={busy}>
            Close
          </Button>
        </header>

        <div ref={fields} className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
          {state === 'done' && (
            <Notice tone="info" title="Already fitted">
              Measured {formatDateTime(wearer.fittingCompletedOn)}
              {wearer.fittingByEmployeeName ? ` by ${wearer.fittingByEmployeeName}` : ''}. Recording
              a later one corrects it; there is no un-fitting somebody.
            </Notice>
          )}

          <div>
            <span className="mb-1.5 block text-[12px] font-medium text-gray-900">
              What happened
            </span>
            <div className="flex flex-wrap gap-1.5">
              {(['schedule', 'complete', 'clear'] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  disabled={busy}
                  onClick={() => setIntent(option)}
                  className={`h-8 cursor-pointer rounded border px-3 text-[12.5px] ${
                    intent === option
                      ? 'border-primary bg-primary text-white'
                      : 'border-border-color hover:bg-light bg-white text-gray-900'
                  }`}
                >
                  {intentLabels[option]}
                </button>
              ))}
            </div>
          </div>

          {intent !== 'clear' && (
            <>
              <Field
                label="Appointment"
                required={intent === 'schedule'}
                hint="When they are due in. One appointment covers everything this person is wearing."
              >
                {/* The app's own picker rather than `type="datetime-local"`, which renders in the
                    browser's locale and so ignores the company's chosen format entirely. */}
                <DatePicker
                  className="w-full"
                  showTime
                  format={dateTimeFormat()}
                  value={toDayjs(appointmentOn) ?? null}
                  disabled={busy}
                  onChange={(value) =>
                    setAppointmentOn(value === null ? null : value.toISOString())
                  }
                />
              </Field>

              <Field
                label="Fitted by"
                hint="Optional — a shop of three does not assign anybody, they simply do it."
              >
                <SearchPicker
                  value={chosenFitter}
                  options={options}
                  total={staff.data?.total ?? 0}
                  loading={staff.isLoading}
                  disabled={busy}
                  placeholder="Search staff"
                  onSearch={setSearch}
                  onSelect={(option) => {
                    setFitterId(option === null ? null : Number(option.id));
                    setFitterName(option?.primary ?? null);
                  }}
                />
              </Field>
            </>
          )}

          {intent === 'clear' && (
            <Notice tone="warning" title="The appointment comes off">
              This person goes back to having no fitting booked, and reappears as outstanding on the
              party. It does not undo a fitting that has already happened.
            </Notice>
          )}

          {problems.map((problem) => (
            <Notice key={problem} tone="warning" title="Not yet">
              {problem}
            </Notice>
          ))}
        </div>

        <footer className="border-border-color flex justify-end gap-2 border-t bg-white px-5 py-3">
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            data-enter-key-commit
            shortcut="save"
            loading={busy}
            disabled={busy || problems.length > 0}
            onClick={() =>
              onRecord(
                fittingRequest(intent, {
                  appointmentOn,
                  // Now, rather than a field somebody has to fill in. A clerk marking a fitting done
                  // is standing beside the person they have just measured; asking them to type the
                  // time is how the time comes to be wrong.
                  completedOn: new Date().toISOString(),
                  byEmployeeId: fitterId,
                }),
              )
            }
          >
            {intentLabels[intent]}
          </Button>
        </footer>
      </aside>
    </div>
  );
}

const intentLabels: Record<Intent, string> = {
  schedule: 'Book them in',
  complete: 'Mark fitted',
  clear: 'Cancel the fitting',
};
